import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { referenceApi } from '@/api/endpoints'
import { Button, EnterChip } from '@/components/ui/Button'
import { Combobox, MultiSelect } from '@/components/ui/Select'
import { YesNo } from '@/components/ui/Toggle'
import { useEnterToContinue, useSaveStep } from '../useWizard'
import { ChoiceWrap, WizardActions } from '../WizardShell'
import type { AuthorizationBasis, WorkEligibilityPayload } from '@/types/api'

interface CountryEntry {
  countryCode: string
  countryName: string
  legallyAuthorized: boolean
  requiresSponsorship: boolean
  authorizationBasis: AuthorizationBasis | null
}

interface Props {
  onDone: () => void
  initial?: Partial<WorkEligibilityPayload>
}

export function WorkEligibilityStep({ onDone, initial }: Props) {
  const save = useSaveStep('work_eligibility')

  const [citizenships, setCitizenships] = useState<string[]>(
    initial?.citizenshipCountryCodes ?? [],
  )
  const [entries, setEntries] = useState<CountryEntry[]>(
    (initial?.workAuthorizations ?? []).map((row) => ({
      countryCode: row.countryCode,
      countryName: row.countryName,
      legallyAuthorized: row.legallyAuthorized ?? true,
      requiresSponsorship: row.requiresSponsorship ?? false,
      authorizationBasis: row.authorizationBasis ?? null,
    })),
  )

  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: () => referenceApi.countries(),
    staleTime: Infinity,
  })
  const { data: reference } = useQuery({
    queryKey: ['reference'],
    queryFn: referenceApi.all,
    staleTime: Infinity,
  })

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.code,
        label: country.name,
        prefix: <span aria-hidden="true">{country.flag}</span>,
      })),
    [countries],
  )

  const addCountry = (code: string) => {
    if (entries.some((entry) => entry.countryCode === code)) return
    const country = countries.find((c) => c.code === code)
    setEntries((current) => [
      ...current,
      {
        countryCode: code,
        countryName: country?.name ?? code,
        // Citizenship of a country implies the right to work in it, so lead
        // with the answer the user would almost certainly give.
        legallyAuthorized: citizenships.includes(code),
        requiresSponsorship: !citizenships.includes(code),
        authorizationBasis: citizenships.includes(code) ? 'citizen' : null,
      },
    ])
  }

  const update = (code: string, patch: Partial<CountryEntry>) =>
    setEntries((current) =>
      current.map((entry) => (entry.countryCode === code ? { ...entry, ...patch } : entry)),
    )

  const remove = (code: string) =>
    setEntries((current) => current.filter((entry) => entry.countryCode !== code))

  const canSubmit = entries.length > 0
  const submit = () => {
    if (!canSubmit || save.isPending) return
    save.mutate(
      { citizenshipCountryCodes: citizenships, workAuthorizations: entries },
      { onSuccess: onDone },
    )
  }
  useEnterToContinue(submit, canSubmit)

  const availableToAdd = countryOptions.filter(
    (option) => !entries.some((entry) => entry.countryCode === option.value),
  )

  return (
    <>
      <div className="space-y-7">
        <MultiSelect
          label="Countries of citizenship"
          options={countryOptions}
          values={citizenships}
          onChange={setCitizenships}
          placeholder="Search and add a country"
          hint="Select every country where you hold citizenship."
          maxItems={10}
        />

        <Combobox
          label="Countries where you want to work"
          appearance="box"
          options={availableToAdd}
          value={null}
          onChange={addCountry}
          placeholder="Search and add a country"
          required
          error={entries.length === 0 ? save.error?.fieldErrors.workAuthorizations ?? null : null}
        />

        {entries.length > 0 && (
          <section>
            <p className="label-eyebrow mb-3">{entries.length === 1 ? 'Added country' : 'Added countries'}</p>
            <div className="space-y-4">
              {entries.map((entry) => (
                <article key={entry.countryCode} className="panel overflow-hidden">
                  <header className="flex items-center justify-between gap-3 border-b border-line bg-surface-muted/70 py-1.5 pl-4 pr-2 sm:pl-5">
                    <h3 className="truncate text-[15px] font-semibold text-ink">{entry.countryName}</h3>
                    <button
                      type="button"
                      onClick={() => remove(entry.countryCode)}
                      className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-field px-2 text-[13px] font-medium text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger"
                    >
                      <span aria-hidden="true">×</span> Remove
                    </button>
                  </header>

                  <div className="divide-y divide-line">
                    <Question label={`Are you legally authorized to work in ${entry.countryName}?`}>
                      <ChoiceWrap>
                        <YesNo
                          value={entry.legallyAuthorized}
                          onChange={(value) => update(entry.countryCode, { legallyAuthorized: value })}
                          aria-label={`Legally authorized to work in ${entry.countryName}`}
                        />
                      </ChoiceWrap>
                    </Question>

                    <Question
                      label={`Will you now or in the future require employer sponsorship in ${entry.countryName}?`}
                    >
                      <ChoiceWrap>
                        <YesNo
                          value={entry.requiresSponsorship}
                          onChange={(value) =>
                            update(entry.countryCode, { requiresSponsorship: value })
                          }
                          aria-label={`Requires sponsorship in ${entry.countryName}`}
                        />
                      </ChoiceWrap>
                    </Question>

                    <div className="px-4 py-4 sm:px-5">
                      <Combobox
                        label="Authorization basis"
                        appearance="box"
                        options={reference?.authorizationBasis ?? []}
                        value={entry.authorizationBasis}
                        onChange={(value) =>
                          update(entry.countryCode, {
                            authorizationBasis: value as AuthorizationBasis,
                          })
                        }
                        placeholder="Select a status"
                        searchable={false}
                      />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>

      <WizardActions>
        <Button
          size="lg"
          className="flex-1 sm:flex-none"
          onClick={submit}
          disabled={!canSubmit}
          loading={save.isPending}
        >
          Continue
          <EnterChip />
        </Button>
      </WizardActions>
    </>
  )
}

/** Label above the control on phones; side by side from `sm`. */
function Question({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-5">
      <p className="text-[14.5px] leading-snug text-ink-soft">{label}</p>
      <div className="w-full sm:w-auto sm:shrink-0">{children}</div>
    </div>
  )
}
